import { find, get, pickBy, reduce } from 'lodash';
import { INFERRED_TYPE_ENUM } from 'constants/chart-channels';

function findSortingChannel(channels) {
  return find(channels, c => c.sorting && !c.isBinning);
}

export function generateVegaTypeCoercionTransforms(valueField, channels = {}) {
  const c = channels[valueField] || {};
  const isDate =
    (c.transformedType || c.inferredType) === INFERRED_TYPE_ENUM.DATE;
  const formulaExpr = `${isDate ? 'toDate' : 'toNumber'}(datum.${valueField})`;
  const filterExpr = `datum.${valueField} !== null && ${
    isDate ? 'isDate' : '!isNaN'
  }(datum.${valueField})`;
  return [
    // convert values to date/number so it is sorted correctly
    { type: 'formula', expr: formulaExpr, as: valueField },
    { type: 'filter', expr: filterExpr },
  ];
}

export function generateVegaSortingAndLimitingTransforms(channels) {
  const transforms = [];

  const windowSort = {
    field: [],
    order: [],
  };

  const sortingChannel = findSortingChannel(channels);
  if (sortingChannel) {
    const {
      sortField,
      order,
      isSortByValue,
      valueField,
      categoryField,
      limit,
    } = sortingChannel.sorting;

    // sort data in the same window by
    // 'sum' if sorting by aggregated value
    // category otherwise
    windowSort.field.push(sortField);
    windowSort.order.push(order);

    // when sorting by aggregated value, especially when there are multiple series
    // data needs to be grouped by category and calculate the sum of each group
    if (isSortByValue) {
      transforms.push({
        type: 'window',
        ops: ['sum'],
        as: [sortField],
        frame: [null, null],
        fields: [valueField],
        groupby: [categoryField],
      });

      // need to consider category as well here so that
      // each different category will get different rank
      // even when categories have same sum value
      windowSort.field.push(categoryField);
      windowSort.order.push(order);
    }

    if (limit) {
      // use 'dense_rank' to rank each group based on its sum
      // because normal 'rank' will rank each data object instead of group
      transforms.push({
        type: 'window',
        sort: windowSort,
        ops: ['dense_rank'],
        fields: [null],
        as: ['rank'],
      });

      // then for those categories that have same sum value
      // update rank to be the same rank as the first category
      // because all categories with same sum should have same rank
      if (isSortByValue) {
        transforms.push({
          type: 'window',
          groupby: [sortField],
          sort: windowSort,
          ops: ['first_value'],
          fields: ['rank'],
          as: ['rank'],
        });
      }

      // finally, only keep data that's in rank
      transforms.push({
        type: 'filter',
        expr: `datum.rank <= ${limit}`,
      });
    }
  }

  if (sortingChannel) {
    // sort the data, the 'window' transform doesn't change data set
    transforms.push({ type: 'collect', sort: windowSort });
  }

  return transforms;
}

export function generateVegaSortParams(channels) {
  const categoryChannels = Object.keys(
    pickBy(channels, channel => channel.channelType === 'category')
  );

  // sort on category fields in ascending order
  const marks = {
    field: categoryChannels,
    order: Array(categoryChannels.length).fill('ascending'),
  };

  const categoryFieldDomain = reduce(
    categoryChannels,
    (acc, channelName) => {
      const channel = channels[channelName];
      const order = get(channel, 'sorting.order', 'ascending');

      // If we have sorting, we sort on the sortField.
      // If we're binning, we sort on the raw version of the field
      // Otherwise, we use the same field as the channel
      const binningField = channel.isBinning
        ? `${channelName}_raw`
        : channelName;
      const field = get(channel, 'sorting.sortField', binningField);

      return {
        ...acc,
        // min should be fine since we've already bucketed the fields.
        // Vega has a bug with some operations blanking the chart, so only change this after testing
        [channelName]: { op: 'min', field, order },
      };
    },
    {}
  );

  // Resolve edge case where color is assumed to be defined by default
  const defaultColor = { op: 'min', field: 'color', order: 'ascending' };

  return { marks, color: defaultColor, ...categoryFieldDomain };
}
